// Chakra imports
import {
  Box,
  Text,
  SimpleGrid,
  useColorModeValue,
  Icon,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
// Custom components
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect } from "react";
import {
  MdPeople,
  MdSchool,
  MdAttachMoney,
  MdCheckCircle,
} from "react-icons/md";
import { formatNumber } from "utils/format";
import studentsService from "services/studentsService";
import StudentsTable from "./components/StudentsTable";

export default function StudentsManagement() {
  // Chakra Color Mode - All hooks must be called at the top level
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    byCenter: {},
  });
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    total: 0,
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Fetch students data
  useEffect(() => {
    const fetchStudents = async () => {
      setStudentsLoading(true);
      try {
        const skip = (pagination.currentPage - 1) * pagination.itemsPerPage;
        const result = await studentsService.getStudents({
          skip,
          limit: pagination.itemsPerPage,
          search: debouncedSearchQuery || undefined,
        });

        if (result.success && result.data) {
          setStudents(result.data);
          if (result.pagination) {
            setPagination(prev => ({
              ...prev,
              total: result.pagination.total,
            }));
            // Calculate stats from data
            const centers = new Set();
            result.data.forEach(student => {
              if (student.center) {
                centers.add(student.center);
              }
            });
            setStats({
              total: result.pagination.total,
              active: result.pagination.total, // Assuming all are active for now
              inactive: 0,
              byCenter: Object.fromEntries(Array.from(centers).map(c => [c, 0])),
            });
          }
        } else {
          setError(result.error || 'Failed to load students');
        }
      } catch (err) {
        console.error('Error fetching students:', err);
        setError('Failed to load students. Please try again.');
      } finally {
        setStudentsLoading(false);
        setLoading(false);
      }
    };

    fetchStudents();
  }, [pagination.currentPage, pagination.itemsPerPage, debouncedSearchQuery]);

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Alert status="error" borderRadius="15px">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        <Text color={textColor} fontSize='2xl' fontWeight='700' mb='10px'>
          Students Management
        </Text>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap='20px' mb='20px'>
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdPeople} color={brandColor} />
                }
              />
            }
            name='Total Students'
            value={formatNumber(stats.total)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdCheckCircle} color='green.500' />
                }
              />
            }
            name='Active Students'
            value={formatNumber(stats.active)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdSchool} color={brandColor} />
                }
              />
            }
            name='By Center'
            value={formatNumber(Object.keys(stats.byCenter || {}).length)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdAttachMoney} color={brandColor} />
                }
              />
            }
            name='Inactive Students'
            value={formatNumber(stats.inactive)}
          />
        </SimpleGrid>

        {/* Students Table */}
        <Box mt="40px">
          {studentsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
              <Spinner size="lg" color="brand.500" />
            </Box>
          ) : (
            <StudentsTable 
              tableData={students} 
              pagination={pagination}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onPageChange={(page) => setPagination(prev => ({ ...prev, currentPage: page }))}
              onItemsPerPageChange={(itemsPerPage) => setPagination(prev => ({ ...prev, itemsPerPage, currentPage: 1 }))}
            />
          )}
        </Box>
      </Flex>
    </Box>
  );
}
